import { DashboardDefinition, ExecutiveOverview, MyWorkOverview } from './types';
import { getActiveSessionOrganizationCode, getActiveSessionUserId } from '../../shared/api';

const API_BASE = '/api/cockpit';

export const dashboardService = {
  async getDashboards(orgId?: string): Promise<DashboardDefinition[]> {
    const activeOrg = orgId || getActiveSessionOrganizationCode() || 'ORG-XYZ';
    const res = await fetch(`${API_BASE}/dashboards`, {
      headers: {
        'x-organization-id': activeOrg,
        'Content-Type': 'application/json'
      }
    });
    if (!res.ok) throw new Error(`Failed to fetch dashboards: ${res.statusText}`);
    const json = await res.json();
    return json.data || [];
  },

  async getDashboardDetail(code: string, orgId?: string): Promise<DashboardDefinition> {
    const activeOrg = orgId || getActiveSessionOrganizationCode() || 'ORG-XYZ';
    const res = await fetch(`${API_BASE}/dashboards/${encodeURIComponent(code)}`, {
      headers: {
        'x-organization-id': activeOrg,
        'Content-Type': 'application/json'
      }
    });
    if (!res.ok) throw new Error(`Failed to fetch dashboard detail: ${res.statusText}`);
    const json = await res.json();
    return json.data;
  },

  async getDashboardByCode(code: string, orgId?: string): Promise<DashboardDefinition> {
    return this.getDashboardDetail(code, orgId);
  },

  async getExecutiveOverview(orgId?: string): Promise<ExecutiveOverview> {
    const activeOrg = orgId || getActiveSessionOrganizationCode() || 'ORG-XYZ';
    const res = await fetch(`${API_BASE}/executive-overview`, {
      headers: {
        'x-organization-id': activeOrg,
        'Content-Type': 'application/json'
      }
    });
    if (!res.ok) throw new Error(`Failed to fetch executive overview: ${res.statusText}`);
    const json = await res.json();
    return json.data;
  },

  async getMyWork(userId?: string, orgId?: string): Promise<MyWorkOverview> {
    const activeOrg = orgId || getActiveSessionOrganizationCode() || 'ORG-XYZ';
    const activeUser = userId || getActiveSessionUserId() || 'usr_erdal_aydeniz';
    const res = await fetch(`${API_BASE}/my-work`, {
      headers: {
        'x-organization-id': activeOrg,
        'x-user-id': activeUser,
        'Content-Type': 'application/json'
      }
    });
    if (!res.ok) throw new Error(`Failed to fetch my work: ${res.statusText}`);
    const json = await res.json();
    return json.data;
  },

  // Dynamic Report Activity Feed from Database
  async getReportActivities(orgId?: string, userId?: string): Promise<any[]> {
    const activeOrg = orgId || getActiveSessionOrganizationCode() || 'ORG-XYZ';
    const activeUser = userId || getActiveSessionUserId() || 'usr_erdal_aydeniz';
    const res = await fetch(`${API_BASE}/report-activities`, {
      headers: {
        'x-organization-id': activeOrg,
        'x-user-id': activeUser,
        'Content-Type': 'application/json'
      }
    });
    if (!res.ok) throw new Error(`Failed to fetch report activities: ${res.statusText}`);
    const json = await res.json();
    return json.data || [];
  },

  // Dynamic System Activity Feed from Database
  async getSystemActivities(orgId?: string): Promise<any[]> {
    const activeOrg = orgId || getActiveSessionOrganizationCode() || 'ORG-XYZ';
    const res = await fetch(`${API_BASE}/system-activities`, {
      headers: {
        'x-organization-id': activeOrg,
        'Content-Type': 'application/json'
      }
    });
    if (!res.ok) throw new Error(`Failed to fetch system activities: ${res.statusText}`);
    const json = await res.json();
    return json.data || [];
  },

  // Dynamic Dashboard Operational Tables (My Reports, Waiting Reports, Investigations, Audits)
  async getDashboardTables(orgId?: string, userId?: string): Promise<{
    myReports: any[];
    waitingReports: any[];
    openInvestigations: any[];
    upcomingAudits: any[];
  }> {
    const activeOrg = orgId || getActiveSessionOrganizationCode() || 'ORG-XYZ';
    const activeUser = userId || getActiveSessionUserId() || 'usr_erdal_aydeniz';
    const res = await fetch(`${API_BASE}/tables`, {
      headers: {
        'x-organization-id': activeOrg,
        'x-user-id': activeUser,
        'Content-Type': 'application/json'
      }
    });
    if (!res.ok) throw new Error(`Failed to fetch dashboard tables: ${res.statusText}`);
    const json = await res.json();
    return json.data || { myReports: [], waitingReports: [], openInvestigations: [], upcomingAudits: [] };
  },

  // Dynamic Indicator Records Drill-Down for Clickable KPI Cards
  async getIndicatorRecords(indicatorKey: string, orgId?: string): Promise<any[]> {
    const activeOrg = orgId || getActiveSessionOrganizationCode() || 'ORG-XYZ';
    const res = await fetch(`${API_BASE}/indicator-records/${encodeURIComponent(indicatorKey)}`, {
      headers: {
        'x-organization-id': activeOrg,
        'Content-Type': 'application/json'
      }
    });
    if (!res.ok) throw new Error(`Failed to fetch indicator records: ${res.statusText}`);
    const json = await res.json();
    return json.data || [];
  },

  // Dynamic User Notifications
  async getNotifications(orgId?: string, userId?: string): Promise<any[]> {
    const activeOrg = orgId || getActiveSessionOrganizationCode() || 'ORG-XYZ';
    const activeUser = userId || getActiveSessionUserId() || 'usr_erdal_aydeniz';
    const res = await fetch(`${API_BASE}/notifications`, {
      headers: {
        'x-organization-id': activeOrg,
        'x-user-id': activeUser,
        'Content-Type': 'application/json'
      }
    });
    if (!res.ok) throw new Error(`Failed to fetch notifications: ${res.statusText}`);
    const json = await res.json();
    return json.data || [];
  },

  // Dynamic Calendar Due Events
  async getCalendarEvents(orgId?: string, userId?: string): Promise<any[]> {
    const activeOrg = orgId || getActiveSessionOrganizationCode() || 'ORG-XYZ';
    const activeUser = userId || getActiveSessionUserId() || 'usr_erdal_aydeniz';
    const res = await fetch(`${API_BASE}/calendar-events`, {
      headers: {
        'x-organization-id': activeOrg,
        'x-user-id': activeUser,
        'Content-Type': 'application/json'
      }
    });
    if (!res.ok) throw new Error(`Failed to fetch calendar events: ${res.statusText}`);
    const json = await res.json();
    return json.data || [];
  },

  async savePreferences(data: { dashboardId: string; custom_layout_json?: string; hidden_widgets_json?: string }, userId?: string, orgId?: string) {
    const activeOrg = orgId || getActiveSessionOrganizationCode() || 'ORG-XYZ';
    const activeUser = userId || getActiveSessionUserId() || 'usr_erdal_aydeniz';
    const res = await fetch(`${API_BASE}/preferences`, {
      method: 'POST',
      headers: {
        'x-organization-id': activeOrg,
        'x-user-id': activeUser,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    });
    if (!res.ok) throw new Error(`Failed to save preferences: ${res.statusText}`);
    return res.json();
  },

  // SPI Alerts & Missing Data Integration (Items 7, 8, 9)
  async getMissingSpiAlerts(year?: number): Promise<any> {
    const activeOrg = getActiveSessionOrganizationCode() || 'ORG-XYZ';
    const activeUser = getActiveSessionUserId() || 'usr_erdal_aydeniz';
    let storedUser: any = null;
    try {
      const s = localStorage.getItem('auth_user');
      if (s) storedUser = JSON.parse(s);
    } catch {}

    const role = storedUser?.user_type || storedUser?.role || 'SAFETY_MANAGER';
    const dept = storedUser?.department || '';
    const yr = year || new Date().getFullYear();

    try {
      const res = await fetch(`/api/safety/performance/missing-alerts?year=${yr}`, {
        headers: {
          'x-organization-id': activeOrg,
          'x-user-id': activeUser,
          'x-user-role': role,
          'x-user-department': dept,
          'Content-Type': 'application/json',
        },
      });
      if (!res.ok) {
        return { isSmsPersonnel: false, totalMissing: 0, criticalMissing: 0, cadenceCounts: { monthly: 0, quarterly: 0, annual: 0 }, items: [] };
      }
      return await res.json();
    } catch {
      return { isSmsPersonnel: false, totalMissing: 0, criticalMissing: 0, cadenceCounts: { monthly: 0, quarterly: 0, annual: 0 }, items: [] };
    }
  },

  async getActiveSpiThresholdAlerts(year?: number): Promise<any> {
    const activeOrg = getActiveSessionOrganizationCode() || 'ORG-XYZ';
    const activeUser = getActiveSessionUserId() || 'usr_erdal_aydeniz';
    let storedUser: any = null;
    try {
      const s = localStorage.getItem('auth_user');
      if (s) storedUser = JSON.parse(s);
    } catch {}

    const role = storedUser?.user_type || storedUser?.role || 'SAFETY_MANAGER';
    const dept = storedUser?.department || '';
    const yr = year || new Date().getFullYear();

    try {
      const res = await fetch(`/api/safety/performance/threshold-alerts?year=${yr}`, {
        headers: {
          'x-organization-id': activeOrg,
          'x-user-id': activeUser,
          'x-user-role': role,
          'x-user-department': dept,
          'Content-Type': 'application/json',
        },
      });
      if (!res.ok) {
        return { isSmsPersonnel: false, totalAlerts: 0, criticalAlerts: 0, items: [] };
      }
      return await res.json();
    } catch {
      return { isSmsPersonnel: false, totalAlerts: 0, criticalAlerts: 0, items: [] };
    }
  }
};
